import { useEffect } from 'react';
import { Box, FormHelperText, styled, Typography } from '@mui/material';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import { usePortalContext } from '@/contexts/PortalProvider';

import dateUtils from '@/services/DateUtils';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import {
  isAppointmentDateRequired,
  isAppointmentDateTimeRequired,
  isPickupDateTimeRequired,
} from '../schema/reservationForm.schema';
import { TripArrayFieldDateInput } from './TripArrayFieldDateInput';
import { ReasonForTripField } from './ReasonForTripField';
import { TripArrayFieldDateTimeInput } from './TripArrayFieldDateTimeInput';
import { WillCallSelector } from './WillCallSelector';
import { PhoneInputField } from './PhoneInputField';
import { PickupNotesField } from './PickupNotesField';
import { DropoffNotesField } from './DropoffNotesField';
import { PickupAddressField } from './PickupAddressField';
import { DropoffAddressField } from './DropoffAddressField';
import { DropoffAddressValidationMessage } from '../messages/DropoffAddressValidationMessage';
import { LevelOfCareSection } from './LevelOfCareSection';
import { RoundTripReturnTripFields } from './RoundTripReturnTripFields';
import { NeedsApprovalMessage } from '../messages/GurneyNeedsApprovalMessage';
import { CareLevelMismatchMessage } from '../messages/CareLevelMismatchMessage';
import { EstimatedTravelTimeMessage } from '../messages/EstimatedTravelTimeMessage';
import { AppointmentTimeNotAllowedMessage } from '../messages/AppointmentTimeNotAllowedMessage';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import type { TripLegEstimatedPickupContext } from '../machines/tripLegEstimatedPickup.machine';

const PREFIX = 'TripLegFieldsRow';

const classes = {
  container: `${PREFIX}-container`,
  fieldSection: `${PREFIX}-fieldSection`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.fieldSection}`]: {
    marginTop: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

function isAddressFilled(address: unknown): boolean {
  if (typeof address === 'string') return address.trim().length > 0;
  if (address && typeof address === 'object') {
    const a = address as {
      isHeader?: boolean;
      place_id?: string;
      mainText?: string;
      description?: string;
    };
    if (a.isHeader || a.place_id === 'header') return false;
    return Boolean(
      a.place_id?.trim() || a.mainText?.trim() || a.description?.trim(),
    );
  }
  return false;
}

export function TripLegFieldsRow({
  index,
  tripLabel,
  showRoundTripReturnFields = false,
}: {
  index: number;
  tripLabel?: string;
  showRoundTripReturnFields?: boolean;
}) {
  const { t } = useTranslation('common');
  const { control, getFieldState, setValue } =
    useFormContext<ReservationFormInput>();
  const { errors } = useFormState({ control, name: 'genericErrors' });
  const { isMemberPortal } = usePortalContext();
  const genericErrorMessages =
    errors.genericErrors?.message != null
      ? String(errors.genericErrors.message)
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const isWillCall =
    useWatch({
      control,
      name: `trips.${index}.isWillCall`,
    }) ?? false;

  const { fieldRefs } = useReservationFormMachines();
  const estimatedPickupSnapshot = useFieldActorSnapshot(
    fieldRefs.tripLegEstimatedPickupRefs[index],
  );
  const estimatedCtx = (estimatedPickupSnapshot?.context ?? {
    estimatedPickupTime: null,
  }) as TripLegEstimatedPickupContext;
  const estimatedPickupTime = estimatedCtx.estimatedPickupTime;

  const pickupAddress = useWatch({
    control,
    name: `trips.${index}.pickupAddress`,
  });
  const dropoffAddress = useWatch({
    control,
    name: `trips.${index}.dropoffAddress`,
  });

  const appointmentDateTime = useWatch({
    control,
    name: `trips.${index}.appointmentDateTime`,
  }) as DateTime | null | undefined;
  const pickupDateTimeValue = useWatch({
    control,
    name: `trips.${index}.pickupDateTime`,
  }) as DateTime | null | undefined;

  useEffect(() => {
    if (isWillCall) return;
    if (!estimatedPickupTime) return;
    if (!appointmentDateTime?.isValid) return;

    const pickupDateTimePath = `trips.${index}.pickupDateTime` as const;
    const pickupDatePath = `trips.${index}.pickupDate` as const;

    if (
      getFieldState(pickupDateTimePath).isDirty ||
      getFieldState(pickupDatePath).isDirty
    ) {
      return;
    }

    const facilityTimeZone = dateUtils.WORK_TIME_ZONE;
    let basePickupDateTime = appointmentDateTime;
    if (
      dateUtils.shouldDefaultToPreviousDay(
        appointmentDateTime,
        facilityTimeZone,
      )
    ) {
      basePickupDateTime = appointmentDateTime.minus({ days: 1 });
    }
    const calculatedPickup = basePickupDateTime.minus({
      minutes: estimatedPickupTime.totalPickupTime,
    });

    if (
      pickupDateTimeValue instanceof DateTime &&
      pickupDateTimeValue.isValid &&
      pickupDateTimeValue.equals(calculatedPickup)
    ) {
      return;
    }

    setValue(pickupDateTimePath, calculatedPickup, {
      shouldValidate: true,
      shouldDirty: false,
    });
    setValue(pickupDatePath, calculatedPickup.toISODate() ?? '', {
      shouldValidate: true,
      shouldDirty: false,
    });
    fieldRefs.pickupDateTime.send({
      type: 'CHANGE',
      index,
      value: calculatedPickup.toISO() ?? '',
    });
  }, [
    index,
    isWillCall,
    estimatedPickupTime,
    appointmentDateTime,
    pickupDateTimeValue,
    getFieldState,
    setValue,
    fieldRefs.pickupDateTime,
  ]);

  const pickupDateTimeReadOnly =
    isMemberPortal ||
    !isAddressFilled(pickupAddress) ||
    !isAddressFilled(dropoffAddress);

  const reasonForTrip = useWatch({
    control,
    name: `trips.${index}.reasonForTrip`,
  });
  const showTripLegForm =
    !isMemberPortal || Boolean(String(reasonForTrip ?? '').trim());

  return (
    <StyledBox className={classes.container}>
      {tripLabel ? (
        <Typography variant="subtitle1" fontWeight={600}>
          {tripLabel}
        </Typography>
      ) : null}

      {index === 0
        ? genericErrorMessages.map((msg, idx) => (
            <FormHelperText error key={idx}>
              {msg}
            </FormHelperText>
          ))
        : null}

      <ReasonForTripField index={index} />

      {showTripLegForm ? (
        <>
          {isWillCall ? (
            <TripArrayFieldDateInput
              tripField="appointmentDate"
              index={index}
              required={isAppointmentDateRequired(isWillCall)}
              minDate={dateUtils.getDefaultMinTime()}
            />
          ) : (
            <>
              <TripArrayFieldDateTimeInput
                tripField="appointmentDateTime"
                index={index}
                required={isAppointmentDateTimeRequired(isWillCall)}
                minDateTime={dateUtils.getDefaultMinTime()}
              />
              <AppointmentTimeNotAllowedMessage index={index} />
            </>
          )}

          <Box className={classes.fieldSection}>
            <WillCallSelector index={index} />

            {!isWillCall && (
              <TripArrayFieldDateTimeInput
                tripField="pickupDateTime"
                index={index}
                required={isPickupDateTimeRequired(isWillCall)}
                readOnly={pickupDateTimeReadOnly}
              />
            )}
            <EstimatedTravelTimeMessage index={index} />

            <PickupAddressField index={index} />
            <PickupNotesField index={index} />
            <PhoneInputField
              index={index}
              tripField="pickupPhone"
              extensionField="pickupPhoneExtension"
              label={t('member.phone')}
              required
            />
          </Box>

          <Box className={classes.fieldSection}>
            <Typography variant="h5" component="h3">
              {t('member.dropOff')}
            </Typography>

            <DropoffAddressField index={index} />
            <DropoffAddressValidationMessage index={index} />
            <DropoffNotesField index={index} />
            <PhoneInputField
              index={index}
              tripField="dropoffPhone"
              extensionField="dropoffPhoneExtension"
              label={t('member.phone')}
            />
          </Box>

          {showRoundTripReturnFields ? (
            <RoundTripReturnTripFields index={index} />
          ) : null}

          <Box className={classes.fieldSection}>
            <LevelOfCareSection index={index} />
            <NeedsApprovalMessage index={index} />
            <CareLevelMismatchMessage index={index} />
          </Box>
        </>
      ) : null}
    </StyledBox>
  );
}
